import { useState } from 'react'
import { Link } from 'react-router-dom'
import PropTypes from 'prop-types'
import rgba from 'polished/lib/color/rgba'

import Icon from 'components/Icon'
import MainHeaderMenu from 'components/navigation/MainHeaderMenu'
import ActiveAttacksNotification from 'components/navigation/ActiveAttacks/ActiveAttacks'
import HaltAllAttacksButton from 'components/navigation/HaltAllAttacksButton'
import CallToActionBanner from 'components/common/CallToActionBanner'
import Text, { styles as textStyles } from 'components/common/Text'
import theme from 'theme'
import ConfirmationModal from 'components/common/ConfirmationModal'
import TextArea from 'components/common/TextArea'
import useAuth from 'hooks/use-auth'
import { track } from 'track'
import { pluralize } from 'utils/strings'
import toast from 'lib/toast'
import agent from 'agent'

function HaltAllConfirmation({ open, onCancel }) {
  const { currentTeamId } = useAuth()
  const [reason, setReason] = useState('')

  return (
    <ConfirmationModal
      confirmText="Confirm"
      message={
        <TextArea
          data-test-input="reason"
          name="reason"
          placeholder="Optional Reason"
          value={reason}
          onChange={({ target: { value } }) => setReason(value)}
        />
      }
      open={open}
      title="Halt All Attacks"
      onCancel={onCancel}
      onConfirm={async () => {
        try {
          await agent.post(`/halts?teamId=${currentTeamId}`, { reason })
          toast({
            text: 'Attacks successfully halted.',
            type: 'success'
          })

          track('HaltAllAttacks')
        } catch (e) {
          toast({
            type: 'error',
            text:
              'There was an issue halting all attacks. Please try again later.'
          })
        }

        onCancel()
      }}
    />
  )
}

HaltAllConfirmation.propTypes = {
  open: PropTypes.bool,
  onCancel: PropTypes.func
}

const Header: React.FC = ({ children, ...rest }) => {
  const {
    daysUntilContractExpiration,
    contractExpirationImminent,
    isFreeEdition,
    session
  } = useAuth()

  const isATeamMember = session?.teams?.length > 0

  return (
    <header
      css={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        position: 'sticky',
        top: 0,
        height: 'calc(var(--header-height) + var(--info-bar-height))',
        background: theme.colors.white[0],
        borderBottom: theme.border.base,
        marginTop: 0,
        marginRight: 0,
        marginBottom: 0,
        zIndex: theme.zIndices[4],

        [theme.mq[2]]: {
          top: 0
        }
      }}
      {...rest}
    >
      {contractExpirationImminent && (
        <CallToActionBanner
          disableHover
          css={{
            whiteSpace: 'pre',
            backgroundColor: `${rgba('#d8e8ff', isFreeEdition ? 1 : 0.3)}`
          }}
        >
          Your trial will end in {daysUntilContractExpiration}{' '}
          {pluralize('day', daysUntilContractExpiration)}. To keep your existing
          functionality, contact our sales team at{' '}
          <a
            css={{ display: 'inline', fontSize: 'inherit' }}
            href="mailto:sales@gremlin.com"
          >
            sales@gremlin.com
          </a>
          .
        </CallToActionBanner>
      )}
      <div
        css={{
          display: 'flex',
          alignItems: 'center',
          padding: `20px ${theme.space[4]}px 19px ${theme.space[4]}px`,

          [theme.mq[2]]: {
            paddingRight: theme.space[7],
            paddingLeft: theme.space[7]
          }
        }}
      >
        {children}
        <div
          css={{
            display: 'flex',
            alignItems: 'center',
            marginLeft: 'auto'
          }}
        >
          {isATeamMember && <ActiveAttacksNotification />}
          <MainHeaderMenu />

          {/* TODO: remove once the flag is lifted */}
          {isATeamMember && (
            <div css={{ marginLeft: theme.space[3] }}>
              <HaltAllAttacksButton />
            </div>
          )}
        </div>
      </div>
    </header>
  )
}

export default Header

export const HeaderLink: React.FC<{ to: string }> = ({
  to,
  children,
  ...rest
}) => {
  return (
    <Link to={to} {...rest} css={{ textDecoration: 'none' }}>
      <Text
        as="h3"
        color="normal"
        css={{
          '&:hover': {
            color: theme.colors.blue[0]
          }
        }}
      >
        {children}
      </Text>
    </Link>
  )
}

// @NOTE We want to use H1 for accessibility but use the styles of an H3
export const Heading: React.FC = props => {
  return (
    <Text as="h1" css={textStyles['h3']} data-testid="page-title" {...props} />
  )
}

export const HeaderRightChevron: React.FC = props => {
  return (
    <Icon
      css={{
        fontSize: theme.fontSizes[3],
        marginLeft: theme.space[2],
        marginRight: theme.space[2],
        flex: 'none' // Prevent collapsing
      }}
      icon="chevronForward"
      {...props}
    />
  )
}
